import { useEffect, useRef, useCallback } from 'react';

/**
 * Visibility-aware polling hook.
 * - Pauses polling when the browser tab is hidden.
 * - Resumes immediately when the tab becomes visible again.
 * - Fetches once on mount, then on the given interval.
 */
export function useVisibilityPolling(fetchFn, interval, enabled = true) {
  const savedFetch = useRef(fetchFn);
  const intervalRef = useRef(null);

  useEffect(() => {
    savedFetch.current = fetchFn;
  }, [fetchFn]);

  const start = useCallback(() => {
    if (intervalRef.current) return;
    intervalRef.current = setInterval(() => savedFetch.current(), interval);
  }, [interval]);

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled) { stop(); return; }

    // Initial fetch
    savedFetch.current();
    start();

    const onVisibilityChange = () => {
      if (document.hidden) {
        stop();
      } else {
        // Fetch immediately on tab focus, then restart interval
        savedFetch.current();
        start();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, start, stop]);
}
